import { teamCategoryTemplateData } from '../constant/class';
import { GenderEnum, MatchPlayerFormatEnum, TeamCategoryTemplateEnum } from '../types/enums';
import { TournamentCategory } from '../types/tournament';
import { isCategoryActiveFromCount } from './teamCategoryRubberCount';

const templateById = new Map(
  teamCategoryTemplateData.map((row) => [row.template, row]),
);

const templateByName = new Map(
  teamCategoryTemplateData.map((row) => [row.name.toLowerCase(), row]),
);

export function resolveTeamCategoryTemplate(
  category: Pick<TournamentCategory, 'name' | 'categoryTemplate'>,
): (typeof teamCategoryTemplateData)[number] | null {
  if (category.categoryTemplate != null) {
    return templateById.get(category.categoryTemplate as TeamCategoryTemplateEnum) ?? null;
  }
  const fromName = templateByName.get(category.name.trim().toLowerCase());
  return fromName ?? null;
}

export function enrichTournamentCategory(category: TournamentCategory): TournamentCategory {
  const template = resolveTeamCategoryTemplate(category);
  if (!template) {
    return category;
  }
  return {
    ...category,
    categoryTemplate: template.template,
    matchPlayerFormat: template.matchPlayerFormat,
    gender: template.gender,
  };
}

export function enrichTournamentCategories(
  categories: TournamentCategory[],
): TournamentCategory[] {
  return categories.map(enrichTournamentCategory);
}

/** Inactive rows are hidden. A leftover match count would be saved back as active. */
export function zeroInactiveCategoryMatchCounts(
  categories: TournamentCategory[],
): TournamentCategory[] {
  return categories.map((category) =>
    category.isActive === false ? { ...category, numberOfMatches: 0 } : category,
  );
}

export function normalizeLoadedTournamentCategories(
  categories: TournamentCategory[] | null | undefined,
): TournamentCategory[] {
  return zeroInactiveCategoryMatchCounts(enrichTournamentCategories(categories ?? []));
}

export function getTeamCategoryDisplayLabelKey(
  category: Pick<TournamentCategory, 'name' | 'categoryTemplate'>,
): string {
  const template = resolveTeamCategoryTemplate(category);
  return template?.labelKey ?? category.name;
}

/** Locale string for templates 1–6. Any other label stays as stored. */
export function formatTeamCategoryLabel(
  t: (key: string) => string,
  category: Pick<TournamentCategory, 'name' | 'categoryTemplate'> | null | undefined,
  fallback: string,
): string {
  const template = category?.categoryTemplate;
  if (template == null || !templateById.has(template as TeamCategoryTemplateEnum)) {
    return fallback;
  }
  return t(
    getTeamCategoryDisplayLabelKey({
      name: category?.name ?? fallback,
      categoryTemplate: template,
    }),
  );
}

export function getTeamCategoryFormatLabelKey(
  category: Pick<TournamentCategory, 'matchPlayerFormat' | 'name' | 'categoryTemplate'>,
): string | null {
  const format =
    category.matchPlayerFormat ??
    resolveTeamCategoryTemplate(category)?.matchPlayerFormat ??
    null;
  if (format === MatchPlayerFormatEnum.SINGLES) {
    return 'enum-matchPlayerFormatSingles';
  }
  if (format === MatchPlayerFormatEnum.DOUBLES) {
    return 'enum-matchPlayerFormatDoubles';
  }
  return null;
}

export function getTeamCategoryGenderLabelKey(
  category: Pick<TournamentCategory, 'gender' | 'name' | 'categoryTemplate'>,
): string | null {
  const gender =
    category.gender ?? resolveTeamCategoryTemplate(category)?.gender ?? null;
  if (gender === GenderEnum.MALE) {
    return 'enum-categoryGenderMale';
  }
  if (gender === GenderEnum.FEMALE) {
    return 'enum-categoryGenderFemale';
  }
  if (gender === GenderEnum.MIXED) {
    return 'enum-categoryGenderMixed';
  }
  if (gender === GenderEnum.OPEN) {
    return 'enum-categoryGenderOpen';
  }
  return null;
}

export function toApiTournamentCategory(item: TournamentCategory): TournamentCategory {
  const enriched = enrichTournamentCategory(item);
  return {
    ...enriched,
    id: typeof enriched.id === 'number' ? enriched.id : undefined,
    isActive: isCategoryActiveFromCount(enriched.numberOfMatches),
    matchPlayerFormat: enriched.matchPlayerFormat ?? null,
    gender: enriched.gender ?? null,
    categoryTemplate: enriched.categoryTemplate ?? null,
  } as TournamentCategory;
}
